/**
 * Who is on the other side of a `/meeting?token=…` page.
 *
 * That one route serves two consumers with opposite needs:
 *
 * 1. **A person** — the in-app PiP panel (which passes the token as a prop), and
 *    the popup used when the app is embedded in Fabric (`?interactive=1`). Here a
 *    microphone is the whole point, and a failure must be surfaced.
 *
 * 2. **A bot** — Recall and Attendee render this exact page as the bot's *camera
 *    feed* inside a customer's Meet/Teams/Zoom call (`output_media.camera.kind:
 *    "webpage"`, see `backend/core/src/intai/routes/meeting_bot.py`). That headless
 *    browser has no microphone and nobody to read an error. Its only job is to play
 *    the bot's voice into the meeting — so blocking the connection on a missing mic
 *    would silence the bot, and rendering a failure banner would paint
 *    "Microphone unavailable" over its face in front of the customer.
 *
 * The backend builds the bot URL as a bare `{webapp_url}?token={token}`, so the
 * ABSENCE of the flag identifies a bot session. Defaulting to non-interactive is
 * the safe direction: a new caller that forgets the flag loses a diagnostic banner,
 * rather than leaking one into someone's meeting.
 *
 * Lives in its own module (not in the route) so it can be imported without pulling
 * in the Pipecat transport, which touches `URL.createObjectURL` at load time.
 */

export const INTERACTIVE_PARAM = 'interactive'

/**
 * @param tokenFromProp token supplied by a mounting component — implies a human.
 * @param search location search string; injectable for tests.
 */
export function isInteractiveSession(
  tokenFromProp?: string | null,
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean {
  if (tokenFromProp != null) return true
  return new URLSearchParams(search).get(INTERACTIVE_PARAM) === '1'
}

/** Append the interactive flag to a `/meeting` URL built for a human. */
export function withInteractiveFlag(url: string): string {
  const parsed = new URL(url, typeof window === 'undefined' ? undefined : window.location.origin)
  parsed.searchParams.set(INTERACTIVE_PARAM, '1')
  return parsed.toString()
}

/**
 * Marks the page as the call HOST: it owns the microphone and the peer connection,
 * but the UI lives elsewhere (the board panel, over `@/lib/callChannel`). Set only
 * on the popup opened from an embedded board.
 *
 * A host renders a deliberately minimal window, because nobody is meant to look at
 * it. Distinct from `interactive` — the standalone in-app call and the Fabric popup
 * are both interactive, but only the popup is a host.
 */
export const CALL_HOST_PARAM = 'host'

export function isCallHostSession(
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean {
  return new URLSearchParams(search).get(CALL_HOST_PARAM) === '1'
}

/** Flags for the popup that hosts a call whose UI is rendered in the board panel. */
export function withCallHostFlags(url: string): string {
  const parsed = new URL(
    withInteractiveFlag(url),
    typeof window === 'undefined' ? undefined : window.location.origin
  )
  parsed.searchParams.set(CALL_HOST_PARAM, '1')
  return parsed.toString()
}
